<script setup lang="ts">
	import { cn } from '@/helpers/cn'
	import { useDebounceFn } from '@vueuse/core'
	import { Ref, onMounted, ref } from 'vue'

	interface Props {
		modelValue?: any
		placeholder?: string
		isRequired?: boolean
		autocomplete?: string
		value?: string
		label?: string
		name?: string
		focus?: boolean
		type?: string
		min?: number
		max?: number
		maxLength?: number
		disabled?: boolean
		inputClass?: string
		error?: string | Ref<string>
		onModelChange?: (value: any) => void
		onInput?: (value: any) => void
		debounceTime?: number
	}

	interface Emits {
		(event: 'update:modelValue', value: any): void
		(e: 'focusIn', value: FocusEvent): void
		(e: 'focusOut', value: FocusEvent): void
	}

	const inputRef = ref<HTMLInputElement | null>(null)
	const props = withDefaults(defineProps<Props>(), {
		type: 'text',
		label: '',
		placeholder: '',
		min: 0,
		value: '',
		onModelChange: () => null,
		onInput: () => null,
		debounceTime: 0,
	})
	const emits = defineEmits<Emits>()

	const handleEmit = useDebounceFn((e: Event) => {
		const value = (e.target as HTMLInputElement).value

		if (props.modelValue != undefined) {
			emits('update:modelValue', value)
		}

		props.onModelChange && props.onModelChange(value)
		props.onInput && props.onInput(value)
	}, props.debounceTime)

	onMounted(() => {
		if (props.focus) {
			inputRef.value?.focus()
		}

		if (!props.modelValue) {
			emits('update:modelValue', props.value ?? '')
		}
	})
</script>

<template>
	<label class="flex flex-col gap-1">
		<div class="flex gap-1">
			<span
				v-if="label"
				class="text-base font-medium"
				>{{ label }}
				<span
					v-if="isRequired"
					class="text-red-500"
					>*</span
				></span
			>
			<slot name="labelCompnent"></slot>
		</div>
		<input
			ref="inputRef"
			:maxlength="maxLength"
			:type="type"
			:value="modelValue"
			:placeholder="placeholder"
			:autocomplete="autocomplete"
			:name="name"
			:min="min"
			:max="max"
			:disabled="disabled"
			@focusin="(e) => emits('focusIn', e)"
			@focusout="(e) => emits('focusOut', e)"
			:class="[
				cn(
					'w-full rounded-md border-2 border-gray-300 px-2 py-2.5 text-sm focus:ring-gray-200',
					disabled && 'cursor-not-allowed bg-slate-200',
					inputClass
				),
			]"
			@input="handleEmit"
		/>

		<span
			v-if="error"
			class="!text-sm text-red-500"
			>{{ error }}</span
		>
	</label>
</template>

<style scoped>
	/* Chrome, Safari, Edge, Opera */
	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	/* Firefox */
	input[type='number'] {
		-moz-appearance: textfield;
	}

	span {
		font-size: inherit;
	}
</style>
